import { BottomButton } from "@components/BottomButton";
import { Routes } from "@config/Routes";
import { InvitationNotFoundFeature } from "@modules/auth/features/invitation/InvitationNotFound.feature";
import { InvitationResource } from "@modules/auth/features/invitation/resources/Invitation.resource";
import { useFocusEffect, useNavigation } from "@react-navigation/native";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { LoginResource } from "@modules/auth/features/login/resources/Login.resource";
import { AxiosResponse } from "axios";
import { InvitationKanguroPointDetailsFeature } from "@modules/auth/features/invitation/InvitationKanguroPointDetails.feature";
import { InvitationViewModel } from "@viewModels/Invitation.viewModel";
import { Loading } from "@components/Loading";
import AsyncStorage from "@react-native-async-storage/async-storage";
import Toast from "react-native-toast-message";
import { isTypeError, TYPE_ERRORS } from "@utils/errors";
import { View } from "react-native";
import { useAuth } from "@auth/useAuth";
import { getInvitationId, isInvitationURL } from "@utils/url";
import { changeLanguage } from "@config/i18n";
import { getLanguageFromULocale } from "@utils/language";
import { User } from "@auth/AuthContextProvider";

export const InvitationFeature = () => {
  const { t } = useTranslation("invitation");
  const navigation = useNavigation();
  const invitationId = getInvitationId();

  // auth hook
  const { userToken, user, signOut, signInWithProfile } = useAuth();

  // Local state
  const [loading, setLoading] = useState(true);
  const [kanguroPoint, setKanguroPoint] = useState<InvitationViewModel | null>(
    null
  );

  /**
   * - load KP details based on invitationId
   * - update kp local state and stop loading
   * - adapt webapp language based on recepient language
   * - save data in local storage , counterId, isMandatoryDniCollect & signed
   */
  async function loadKpDetailsBasedOnInvitationId() {
    if (!invitationId) return;
    const response = await InvitationResource.getInvitation(invitationId);
    const kpPoint = new InvitationViewModel(response.data);
    setKanguroPoint(kpPoint);

    const language = getLanguageFromULocale(kpPoint.recipient.languageId);
    changeLanguage(language);

    await AsyncStorage.setItem(
      "invitation_ids",
      JSON.stringify({
        counterId: kpPoint.counter?.id,
        isMandatoryDniCollect: kpPoint.isMandatoryDniCollect,
        signed: false,
      })
    );
  }

  /**
   * - if kp.invitationLoginAllowed is true we will do autoLogin
   * - we will use kp.id & kp.recepient.id to login
   * - then we will do signInWithProfile(accessToken, recipient)
   */
  async function autoLogin() {
    if (!kanguroPoint) return;
    const KpId = kanguroPoint.id;
    const recipientId = kanguroPoint.recipient.id;
    if (KpId && recipientId) {
      const res: AxiosResponse<{ accessToken: string; recipient: User }> =
        await InvitationResource.loginViaInvitation(recipientId, KpId);

      if (res.data.recipient.id === recipientId) {
        signInWithProfile(res.data.accessToken, res.data.recipient);
        // @ts-ignore
        isInvitationURL() ? navigation.navigate("Receive") : null;
      } else {
        signOut();
      }
    }
  }

  /**
   * - check the opened url, if contains "email = 1" to know the login method
   * - login method either via recepient Email or phone number
   * - after login we'll get the requestId and navigate to verify via otp page
   */
  async function manualLogin() {
    if (!kanguroPoint) return;
    const viaEmail = window.location.href.includes("email=1");
    const { recipient } = kanguroPoint;
    const call = viaEmail
      ? LoginResource.requestCodeByEmail(recipient.email)
      : LoginResource.requestCode(recipient.phone);

    call
      .then((res: AxiosResponse<{ requestId: string }>) => {
        const data = {
          requestId: res.data.requestId,
          phone: recipient.phone,
          email: viaEmail ? recipient.email : undefined,
        };
        //@ts-ignore
        navigation.navigate(Routes.auth.VERIFY, data);
      })
      .catch((err) => {
        if (isTypeError(err, TYPE_ERRORS.CONCURRENT_VERIFICATION)) {
          Toast.show({
            type: "error",
            text1: t("invitation_error")!,
            text2: t("invitation_error_description")!,
          });
          return;
        }
      });
  }

  const handleNext = () => {
    if (!kanguroPoint) return;
    if (userToken) {
      const userId = user.id;
      const recepientId = kanguroPoint.recipient.id;
      if (userId !== recepientId) {
        signOut();
        manualLogin();
      } else {
        // @ts-ignore
        isInvitationURL() ? navigation.navigate("Receive") : null;
      }
    } else {
      manualLogin();
    }
  };

  useFocusEffect(
    React.useCallback(() => {
      if (!invitationId) return;
      setLoading(true);
      loadKpDetailsBasedOnInvitationId().finally(() => setLoading(false));
    }, [invitationId])
  );

  if (loading) {
    return <Loading />;
  }

  if (!kanguroPoint) {
    return <InvitationNotFoundFeature />;
  }

  return (
    <View style={{ height: "100%" }}>
      <InvitationKanguroPointDetailsFeature kanguroPoint={kanguroPoint} />
      <BottomButton
        onPress={() => {
          if (kanguroPoint.invitationLoginAllowed) {
            autoLogin();
          } else {
            handleNext();
          }
        }}
        text={
          !!kanguroPoint.locker
            ? t("invitation_locker_submit")
            : t("invitation_counter_submit")
        }
      />
    </View>
  );
};
